import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

const DEFAULT_PAGES = [
  { slug: "home", title: "Home", path: "/", show_in_menu: true, menu_order: 0, builtin: true },
  { slug: "channel", title: "Cresway Channel", path: "/channel", show_in_menu: true, menu_order: 10, builtin: true },
  { slug: "check-status", title: "Check Submission Status", path: "/check-status", show_in_menu: true, menu_order: 20, builtin: true },
  { slug: "fan-mail", title: "Fan Mail Portal", path: "/fan-mail", show_in_menu: true, menu_order: 30, builtin: true },
  { slug: "contact", title: "Contact Us", path: "/contact", show_in_menu: true, menu_order: 100, builtin: true },
];

const AdminCtx = createContext(null);
export const useAdmin = () => useContext(AdminCtx);

export const AdminProvider = ({ children }) => {
  const [user, setUser] = useState(localStorage.getItem("cm_admin_token") ? null : false);
  const [editing, setEditing] = useState(false);
  const [siteMode, setSiteModeState] = useState(() => { try { return sessionStorage.getItem("cm_site_mode") === "1"; } catch (e) { return false; } });
  const setSiteMode = useCallback((on) => {
    try { if (on) sessionStorage.setItem("cm_site_mode", "1"); else sessionStorage.removeItem("cm_site_mode"); } catch (e) { /* private mode */ }
    setSiteModeState(on);
    setEditing(on);
  }, []);
  const navigate = useNavigate();
  const setLoginOpen = useCallback((open) => { if (open) navigate("/login"); }, [navigate]);
  const [pages, setPages] = useState(DEFAULT_PAGES);
  const [pagesLoaded, setPagesLoaded] = useState(false);
  const [content, setContent] = useState({});

  const refreshPages = useCallback(async () => {
    try {
      const { data } = await api.get("/pages");
      if (Array.isArray(data) && data.length) {
        const merged = new Map(DEFAULT_PAGES.map((page) => [page.slug, page]));
        data.forEach((page) => {
          const fallback = merged.get(page.slug);
          merged.set(page.slug, fallback
            ? { ...fallback, ...page, show_in_menu: page.show_in_menu ?? fallback.show_in_menu }
            : page);
        });
        setPages([...merged.values()].sort((a, b) => (a.menu_order || 0) - (b.menu_order || 0)));
      }
    } catch (e) {
      setPages((p) => p);
    } finally {
      setPagesLoaded(true);
    }
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const { data: account } = await api.get("/auth/me");
      setUser(account);
    } catch {
      localStorage.removeItem("cm_admin_token");
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refreshPages();
    api.get("/content").then((r) => setContent(r.data && typeof r.data === "object" ? r.data : {})).catch(() => {});
    if (localStorage.getItem("cm_admin_token")) refreshMe();
  }, [refreshPages, refreshMe]);

  const startSession = async (token) => {
    localStorage.setItem("cm_admin_token", token);
    const me = await api.get("/auth/me");
    setUser(me.data);
    setSiteMode(false);
    return me.data;
  };
  const login = async (email, password) => {
    const device_token = localStorage.getItem("cm_device_token") || undefined;
    const { data } = await api.post("/auth/login", { email, password, device_token });
    if (data?.verify_required) return data;
    return startSession(data.token);
  };
  const verifyLogin = async (challenge, code, remember = false) => {
    const { data } = await api.post("/auth/login/verify", { challenge, code, remember });
    if (data?.device_token) localStorage.setItem("cm_device_token", data.device_token);
    return startSession(data.token);
  };

  const logout = () => {
    localStorage.removeItem("cm_admin_token");
    setUser(false);
    setSiteMode(false);
  };

  const saveContent = async (key, value) => {
    setContent((c) => ({ ...c, [key]: value }));
    await api.put("/content", { [key]: value });
  };

  const isAdmin = user?.role === "admin";
  const isStaff = user?.role === "staff";
  const isOwner = Boolean(user?.is_owner);
  const can = (section) => isAdmin || (Array.isArray(user?.sections) && user.sections.includes(section));
  const canEdit = can("edit_website");
  return (
    <AdminCtx.Provider value={{ user, isAdmin, isStaff, isOwner, can, canEdit, refreshMe, siteMode: canEdit && siteMode, setSiteMode, editing: canEdit && siteMode && editing, setEditing, setLoginOpen, login, verifyLogin, logout, pages, pagesLoaded, refreshPages, content, saveContent }}>
      {children}
    </AdminCtx.Provider>
  );
};
